import { Box, Text, Link } from "flicket-ui";

import { ReleaseType } from "~graphql/sdk";
import { CreateOrderFn } from "~hooks";
import {
  INormalisedData,
  IReleaseTicket,
  IReleaseZone,
} from "~features/nonseated-reservation/nonseated-reservation.types";
import { ExperienceSalesFooter } from "~features/nonseated-reservation/components/ExperienceSalesFooter/ExperienceSalesFooter";
import { ReservationList } from "~components/reservation/non-seated";
import { Icon } from "~components/common";
import HeaderAlertBanner from "~components/common/common.HeaderAlertBanner";
import { useRouter } from "next/router";
import { ArrowLeft } from "phosphor-react";
import { Footer } from "~components";
import { useIntersectZone } from "./useIntersection.hook";
import Image from "~components/common/Image";
import { StickyZoneTabs } from "./StickyZoneTabs";
import { UserContext } from "~context";
import { useContext } from "react";
import {
  ImportantNotice,
  R18Notice,
} from "~components/reservation/non-seated/non-seated.components";
import { CONTENT_MAX_WIDTH } from "./non-seated.constants";
import {} from "~components/reservation/non-seated/non-seated.helpers";
import { useNonSeatedReservationStore } from "~features/nonseated-reservation/store/non-seated-reservation-store";
import { useGetTicketsSoldForUser } from "~components/reservation/non-seated/useGetTicketsSoldForUser.hook";
import { useFilterVisibleZonesAndTickets } from "~components/reservation/non-seated/useFilterVisibleZonesAndTickets.hook";
import { removeUnsyncedQueryParams } from "~lib/state-stores/appStore/action.removeUnsyncedQueryParams";

type NonSeatedProps = {
  data: INormalisedData;
  zones: IReleaseZone[];
  addons: IReleaseTicket[];
  createOrder: CreateOrderFn;
  isMembership: boolean;
  isAdmin: boolean;
  releaseId?: string;
  membershipId?: string;
  releaseNotes: string;
  allowPublicComp: boolean;
  isResaleRelease: boolean;
  releaseType: ReleaseType;
  requiresAuth: boolean;
};

export const NonSeatedReservationPage = ({
  addons,
  data,
  zones,
  createOrder,
  isMembership = false,
  isAdmin,
  releaseId,
  membershipId,
  releaseNotes,
  allowPublicComp,
  isResaleRelease,
  releaseType,
  requiresAuth = false,
}: NonSeatedProps) => {
  const { isPOS, authState } = useContext(UserContext);

  const selectedTickets = [];
  useNonSeatedReservationStore((state) => state.selectedTicketTypes[data?.id]);

  const { ticketsSoldForUser } = useGetTicketsSoldForUser({
    data,
    isMembership,
    isLoggedIn: authState === "authenticated",
  });

  const visibleZonesAndTickets = useFilterVisibleZonesAndTickets({
    zones,
    selectedTickets,
    ticketsSoldForUser,
    isPresaleRelease: releaseType === ReleaseType.Presale,
    isAdmin,
  });

  // This intersection observer is used to remove the zoneId query param
  // when the user scrolls back to the top of the page.
  const { ref: intersectionRef } = useIntersectZone(
    {
      onIntersectTop: () => {
        removeUnsyncedQueryParams(["zoneId"]);
      },
    },
    {
      threshold: [0, 1],
    }
  );

  return (
    <Box width={1}>
      <Box ref={intersectionRef}>
        <Box
          zIndex="banner"
          css={`
            position: fixed;
            top: 0;
            left: 0;
            right: 0;
          `}
        >
          <HeaderAlertBanner />
        </Box>

        <Box pt={{ sm: 3 }} />

        <Box
          width={1}
          px={{ _: 0, sm: 2 }}
          maxW={{ sm: CONTENT_MAX_WIDTH }}
          margin={"auto" as any}
          mb={4}
          minHeight="60px"
        >
          <Box position="relative">
            {data.banner && (
              <Image
                image={data.banner}
                alt={data.name}
                width={{ _: "100%" }}
                borderRadius={{ sm: "sm" }}
                backgroundColor="N200"
              />
            )}

            <Link
              href={`/events/${data.id}`}
              display="inline-flex"
              justifyContent="center"
              position="absolute"
              top={2}
              left={2}
              width={40}
              height={40}
              borderRadius="full"
              css={`
                opacity: 1;
                transition: none;
                background-color: rgba(0, 0, 0, 0.7);

                &:hover {
                  opacity: 1;
                  background-color: rgba(0, 0, 0, 1);
                }
              `}
            >
              <Icon
                icon={<ArrowLeft size={20} weight="bold" />}
                color="white"
              />
            </Link>
          </Box>
        </Box>

        <Box
          width={1}
          px={2}
          maxW={{ sm: CONTENT_MAX_WIDTH }}
          margin={"auto" as any}
          mb={2}
        >
          <Text variant="header.L" mb="1/2">
            {data.name}
          </Text>
          <Text variant="regular">
            {data.displayDate} • {data.location}
          </Text>

          <Box>
            {data.isR18 && <R18Notice />}
            {!isPOS && <ImportantNotice value={data.importantNotice} />}
          </Box>
        </Box>
      </Box>

      <StickyZoneTabs zones={visibleZonesAndTickets} addons={addons} />

      <Box
        width={1}
        px={2}
        maxW={{ sm: CONTENT_MAX_WIDTH }}
        margin={"auto" as any}
      >
        <ReservationList
          addons={addons}
          data={data}
          zones={zones}
          createOrder={createOrder}
          isMembership={isMembership}
          releaseNotes={releaseNotes}
          isDigital={data.isDigital}
          allowPublicComp={allowPublicComp}
          isResaleRelease={isResaleRelease}
          releaseType={releaseType}
          requiresAuth={requiresAuth}
        />
      </Box>

      <ExperienceSalesFooter
        id={data.id}
        releaseId={releaseId}
        membershipId={membershipId}
        hideFooterDuringMode={["no-selection"]}
      />

      <Footer narrow={true} display={{ _: "flex", md: "flex" }} />
    </Box>
  );
};
